// Updated PendingServiceOrders.tsx - Handle tab visibility in component
import { useState, useMemo, useCallback, useEffect } from "react";
import { Button } from "@shared/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@shared/ui/toggle-group";
import { useSearchParams } from "react-router-dom";
import {
  UserPlus,
  Eye,
  Trash2,
  TrendingUp,
  Clock,
  AlertTriangle,
  XCircle,
  Users,
  Plus,
  Map,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";

// Updated imports - using simplified hooks
import {
  useDownloadReport,
  usePendingServiceOrdersExport,
  usePendingServiceOrdersWithFilters,
  usePendingSummary,
} from "../../hooks";
import { serviceOrderFilters } from "../../types";

// Import existing components
import { ServiceOrderFilterPanel } from "../../../dispatcher/components/filters/filters";
import { getPriorityColor, getStatusColor, formatDate } from "../../../utils";
import { SingleTechnicianAssignmentModal } from "../assigned/bulk/assignment/individualTechnician/SingleTechnicianAssignmentModal";
import { BulkAssignDialog } from "../assigned/bulk/dialog/BulkAssignDialog";
import {
  PendingServiceOrdersProps,
  TransformedServiceOrder,
} from "../../types";
import { GenericTable } from "@shared/components/GenericTable";
import { ServiceOrderMapView } from "./view/ServiceOrderMapView";

export const PendingServiceOrders: React.FC<PendingServiceOrdersProps> = ({
  onCreateServiceOrder,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const isActiveTab = "pending";

  const [viewMode, setViewMode] = useState<"list" | "map">("list");
  const [selectedOrderForAssignment, setSelectedOrderForAssignment] =
    useState<TransformedServiceOrder | null>(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isBulkAssignDialogOpen, setIsBulkAssignDialogOpen] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [selectedOrdersData, setSelectedOrdersData] = useState<any[]>([]);

  // Clear selections when tab becomes active (URL changes to show only tab and page)
  useEffect(() => {
    const currentTab = searchParams.get("tab");
    const paramsCount = Array.from(searchParams.keys()).length;

    // If we're on the pending tab and only have tab + page params, clear selections
    if (currentTab === "pending" && paramsCount <= 2) {
      setSelectedOrders([]);
      setSelectedOrdersData([]);
      setViewMode("list"); // Reset to list view
    }
  }, [searchParams]);

  const {
    serviceOrders,
    isLoading,
    pagination,
    currentFilters,
    handleSearch,
    handlePageChange,
    handleApplyFilters,
    handleResetFilters,
    refetch,
  } = usePendingServiceOrdersWithFilters();

  const {
    data: summaryData,
    isLoading: summaryLoading,
    error: summaryError,
  } = usePendingSummary();

  const { exportCSV, isExporting } =
    usePendingServiceOrdersExport(currentFilters);

  const handleExport = () => {
    exportCSV();
  };

  // Debug logging
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  useEffect(() => {
    refetch(); // Just refetch the current query
  }, [location.pathname]);

  const currentMonth = monthNames[new Date().getMonth()];

  // Get filter values from URL - these will be empty when tab changes
  const searchQuery = searchParams.get("query") || "";
  const statusFilter = searchParams.get("status") || "all";
  const serviceAssociationFilter =
    searchParams.get("serviceAssociation") || "all";
  const priorityFilter = searchParams.get("priority") || "all";
  const areaFilter = searchParams.get("area") || "all";

  const kpis = useMemo(() => {
    return {
      totalServiceOrders: summaryData?.total ?? 0,
      totalCreatedThisMonth: summaryData?.created ?? 0,
      overdueServiceOrders: summaryData?.overdue ?? 0,
      refusedServiceOrders: summaryData?.refused ?? 0,
    };
  }, [summaryData]);

  // KPI Cards configuration using QuickStatsCard
  const serviceOrderKpiCards = [
    {
      title: "Total Service Orders",
      count: kpis.totalServiceOrders,
      trendSummary: "All pending service orders in the system",
      trendDirection: "down" as const,
      icon: <TrendingUp className="h-8 w-8 text-blue-500" />,
    },
    {
      title: "Created This Month",
      count: kpis.totalCreatedThisMonth,
      trendSummary: `New orders created in ${currentMonth}`,
      trendDirection: "up" as const,
      icon: <Clock className="h-8 w-8 text-green-500" />,
    },
    {
      title: "Overdue Service Orders",
      count: kpis.overdueServiceOrders,
      trendSummary: "Orders past their deadline requiring immediate attention",
      trendDirection: "down" as const,
      icon: <AlertTriangle className="h-8 w-8 text-red-500" />,
    },
    {
      title: "Refused Service Orders",
      count: kpis.refusedServiceOrders,
      trendSummary: "Orders declined by technicians needing reassignment",
      trendDirection: "down" as const,
      icon: <XCircle className="h-8 w-8 text-orange-500" />,
    },
  ];

  const handleSelectionChange = (selectedIds: string[]) => {
    setSelectedOrders(selectedIds);

    const selectedData = serviceOrders.filter((item) => {
      const itemId = String(item.id);
      const itemSoId = String(item.soId || "");
      return selectedIds.includes(itemId) || selectedIds.includes(itemSoId);
    });

    setSelectedOrdersData(selectedData);
  };

  // Selected orders for map with coordinates
  const selectedOrdersForMap = useMemo(() => {
    return selectedOrdersData.map((order) => {
      return {
        id: order.id,
        job_card_no: order.soId || order.job_card_no,
        name: order.name || order.sop_name,
        serviceAssociation: order.serviceAssociation,
        priority: order.priorityDisplay,
        area: order.area,
        subArea: order.subArea,
        status: order.status,
        statusDisplay: order.statusDisplay,
        createdOn: order.createdOn,
        latitude: order.latitude || null,
        longitude: order.longitude || null,
        mobile_number: order.mobile_number,
        request_account_no: order.request_account_no,
      };
    });
  }, [selectedOrdersData]);

  const tableColumns = [
    {
      key: "soId",
      header: "SO ID",
      sortable: true,
      render: (value: string) => (
        <span className="text-blue-600 font-medium">{value}</span>
      ),
    },
    {
      key: "name",
      header: "SO Name",
      sortable: true,
      render: (value: string) => (
        <span className="text-gray-900 font-medium">{value}</span>
      ),
    },
    {
      key: "priority",
      header: "Priority",
      sortable: true,
      type: "badge" as const,
      badgeConfig: {
        getClassName: (value: string) => getPriorityColor(value),
      },
    },
    {
      key: "areaName",
      header: "Area",
      sortable: true,
      render: (value: string) => <span className="text-gray-700">{value ?? "NA"}</span>,
    },
    {
      key: "subAreaName",
      header: "Sub Area",
      render: (value: string) => <span className="text-gray-700">{value ?? "NA"}</span>,
    },
    {
      key: "serviceAssociation",
      header: "Service Association",
      sortable: true,
      render: (value: string) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {value}
        </span>
      ),
    },
    {
      key: "statusDisplay",
      header: "Status",
      sortable: true,
      type: "badge" as const,
      badgeConfig: {
        getClassName: (value: string) => getStatusColor(value),
      },
    },
    {
      key: "createdOn",
      header: "Created On",
      sortable: true,
      render: (value: string) => <span className="text-gray-700">{value}</span>,
    },
  ];

  const bulkActions = [
    {
      label: "Assign",
      onClick: (selectedData: any[], selectedIds: string[]) => {
        handleBulkAssign();
      },
      icon: <Users className="h-4 w-4" />,
      variant: "outline" as const,
    },
    {
      label: "Show on Map",
      onClick: (selectedData: any[], selectedIds: string[]) => {
        setViewMode("map");
      },
      icon: <Map className="h-4 w-4" />,
      variant: "outline" as const,
    },
  ];

  const tableActions = [
    {
      label: "Assign Technician",
      onClick: (row: any) => handleAssignTechnician(row),
      icon: "UserPlus" as const,
      variant: "ghost" as const,
    },
    {
      label: "View Details",
      onClick: (row: any) => handleViewServiceOrder(row),
      icon: "Eye" as const,
      variant: "ghost" as const,
    },
  ];

  const handleAssignTechnician = (order: TransformedServiceOrder) => {
    setSelectedOrderForAssignment(order);
    setIsAssignModalOpen(true);
  };

  const handleBulkAssign = () => {
    setIsBulkAssignDialogOpen(true);
  };

  const handleViewServiceOrder = (order: any) => {
    const association = order.serviceAssociation?.toLowerCase();

    switch (association) {
      case "meter":
        navigate(
          `/service-orders/dispatcher/pending-meter-service-order/${order.id}`
        );
        break;
      case "consumer":
        navigate(
          `/service-orders/dispatcher/pending-consumer-service-order/${order.id}`
        );
        break;
      case "asset":
        navigate(
          `/service-orders/dispatcher/pending-asset-service-order/Asset/${order.id}`
        );
        break;
      case "facility":
        navigate(
          `/service-orders/dispatcher/pending-asset-service-order/Facility/${order.id}`
        );
        break;
      case "system":
        navigate(
          `/service-orders/dispatcher/pending-asset-service-order/System/${order.id}`
        );
        break;
      case "network":
        navigate(
          `/service-orders/dispatcher/pending-asset-service-order/Network/${order.id}`
        );
        break;
      default:
        navigate(
          `/service-orders/dispatcher/pending-service-order/${order.id}`
        );
        break;
    }
  };

  const handleShowOnMap = () => {
    setViewMode("map");
  };

  const handleClearSelection = () => {
    setSelectedOrders([]);
    setSelectedOrdersData([]);
  };

  // Create current filters object for filterPanel
  const filterPanelFilters: Partial<serviceOrderFilters> = useMemo(() => {
    return {
      status: statusFilter !== "all" ? statusFilter : undefined,
      serviceAssociation:
        serviceAssociationFilter !== "all"
          ? serviceAssociationFilter
          : undefined,
      priority: priorityFilter !== "all" ? priorityFilter : undefined,
      area: areaFilter !== "all" ? areaFilter : undefined,
      searchData: searchQuery,
    };
  }, [
    statusFilter,
    serviceAssociationFilter,
    priorityFilter,
    areaFilter,
    searchQuery,
  ]);

  const resetBulkAssignFlow = () => {
    setIsBulkAssignDialogOpen(false);
    setSelectedOrders([]);
    setSelectedOrdersData([]);
  };

  return (
    <div className="space-y-6">
      {/* Header with buttons */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="page-subheading">Pending Service Orders</h2>
          <p className="page-subtitle">
            Manage and assign pending service orders
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex items-center gap-2"
            onClick={handleBulkAssign}
            disabled={selectedOrders.length === 0}
          >
            <Users className="h-4 w-4" />
            Assign ({selectedOrders.length})
          </Button>
          {selectedOrders.length > 0 && (
            <Button
              variant="outline"
              className="flex items-center gap-2"
              onClick={handleShowOnMap}
            >
              <Map className="h-4 w-4" />
              Show on Map ({selectedOrders.length})
            </Button>
          )}
          <Button
            onClick={onCreateServiceOrder}
          >
            <Plus className="h-4 w-4" />
            Create Service Order
          </Button>
        </div>
      </div>

      {/* KPI Cards - Using loop with QuickStatsCard */}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {serviceOrderKpiCards.map((card, index) => (
          <QuickStatsCard
            key={index}
            title={card.title}
            count={card.count}
            trendSummary={card.trendSummary}
            trendDirection={card.trendDirection}
            icon={card.icon}
          />
        ))}
      </div>

      {/* Selection Status */}
      {selectedOrders.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span className="text-blue-700 font-medium">
                {selectedOrders.length} service order(s) selected
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearSelection}
              className="text-blue-600 hover:text-blue-700"
            >
              Clear selection
            </Button>
          </div>
        </div>
      )}

      {/* Service Order Table with View Toggle */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">
            Service Orders ({pagination?.totalItems || 0})
            {selectedOrders.length > 0 &&
              ` - ${selectedOrders.length} selected`}
          </h3>
          <ToggleGroup
            type="single"
            value={viewMode}
            onValueChange={(value) =>
              value && setViewMode(value as "list" | "map")
            }
          >
            <ToggleGroupItem
              value="list"
              aria-label="List view"
              className="data-[state=on]:bg-blue-500 data-[state=on]:text-white"
            >
              <span className="text-sm font-medium">List</span>
            </ToggleGroupItem>
            <ToggleGroupItem
              value="map"
              aria-label="Map view"
              className="data-[state=on]:bg-blue-500 data-[state=on]:text-white"
            >
              <span className="text-sm font-medium">Map</span>
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        {viewMode === "list" ? (
          <GenericTable
            data={serviceOrders}
            columns={tableColumns}
            rowKey="id"
            selectable={true}
            selectedRows={selectedOrders}
            onSelectionChange={handleSelectionChange}
            actions={tableActions}
            actionsType="dropdown"
            bulkActions={bulkActions}
            loading={isLoading}
            // Search integration
            onSearch={handleSearch}
            searchPlaceholder="Search by SO ID, Name..."
            searchParamName="query"
            searchDebounceMs={300}
            // Filter panel integration
            filterPanel={
              <ServiceOrderFilterPanel
                currentFilters={filterPanelFilters}
                onApplyFilters={handleApplyFilters}
                onResetFilters={handleResetFilters}
                orderType="pending"
              />
            }
            // Pagination
            pagination={pagination}
            onPageChange={handlePageChange}
            onExport={handleExport} // Add this line
            exportLoading={isExporting}
            emptyMessage="No pending service orders found matching your criteria"
          />
        ) : (
          <ServiceOrderMapView
            serviceOrders={selectedOrdersForMap}
            allServiceOrders={serviceOrders}
            onServiceOrderClick={handleViewServiceOrder}
            onClearSelection={handleClearSelection}
            onBackToList={() => setViewMode("list")}
            selectedCount={selectedOrders.length}
          />
        )}
      </div>

      {/* Modals */}
      <SingleTechnicianAssignmentModal
        open={isAssignModalOpen}
        onOpenChange={setIsAssignModalOpen}
        serviceOrder={selectedOrderForAssignment}
      />

      <BulkAssignDialog
        open={isBulkAssignDialogOpen}
        onOpenChange={setIsBulkAssignDialogOpen}
        selectedOrders={selectedOrders}
        selectedOrdersData={selectedOrdersData}
        sopData={selectedOrdersData.map((order) => ({
          id: order.id,
          sop: order.sop,
          sopName: order.sopName,
          sopAssociationType: order.sopAssociationType,
          sopAssociationTypeDisplay: order.sopAssociationTypeDisplay,
          serviceAssociation: order.serviceAssociation,
          sopUtilityService: order.sopUtilityService,
        }))}
        onComplete={resetBulkAssignFlow}
      />
    </div>
  );
};
